import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMember } from "@/contexts/memberContext";
import { getPaletteBySwatch } from "@/lib/paletteBySwatch";
import { getAmenityColumnLabel } from "@/lib/utils";
import { CircleQuestionMark, Lock } from "lucide-react";
import type { Amenity } from "@/types/Amenity";
import type { Room } from "@/types/Room";

// Helper to check if a room is shuttered on a specific date
const isRoomShutteredOnDate = (room: Room, date: Date): boolean => {
  if (!room.shutterUntil) return false;
  const shutterDate = new Date(room.shutterUntil);

  // Check if the START of the day is before the shutterUntil time
  // This way, if any part of the day has shuttered time slots, the header shows as closed
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  // Also check if the end of the day is after the start time (i.e., some overlap exists)
  // If start of day is before shutterUntil, then at least some slots on this day are shuttered
  return startOfDay < shutterDate;
};

interface RoomColumnHeadersProps {
  rooms: Room[];
  date: Date;
  onRoomClick?: (room: Room) => void;
}

export const RoomColumnHeaders = ({ rooms, date, onRoomClick }: RoomColumnHeadersProps) => {
  const { member } = useMember();
  return (
    <>
      {rooms.map((room) => {
        const shuttered = isRoomShutteredOnDate(room, date);
        const _bg = shuttered
          ? "bg-gray-300"
          : getPaletteBySwatch(room.swatch, { discoModeEnabled: member?.settings?.discoModeEnabled })?.shoulder || "bg-gray-100/10 dark:bg-slate-700";
        // Three-tier responsive header (container queries):
        //  - Wide  (≥ 6rem / 96px): horizontal name + info icon.
        //  - Tight (2.5rem – 6rem):  name rotated to vertical-rl so narrow columns still label.
        //  - Narrow (< 2.5rem): coloured bar only — nothing else fits cleanly.
        return (
          <div
            className={`@container text-center text-sm flex-1 truncate overflow-hidden ${_bg} hover:cursor-pointer ${shuttered ? "opacity-60" : ""}`}
            key={room.id}
            aria-label={`Column showing ${room.name}'s schedule. Click for room information.`}
            title={shuttered ? "Room is closed" : room.name}
            onClick={() => onRoomClick?.(room)}
          >
            <div className="pt-1 h-full flex justify-center items-center gap-0.5">
              {shuttered && <Lock className="w-3 h-3 text-red-600" />}
              {/* Horizontal label — only at ≥ 6rem column width. */}
              <span className={`hidden @[6rem]:inline truncate ${shuttered ? "text-gray-600" : ""}`}>{room.name}</span>
              {/* Vertical label — appears in the tight range, hidden below 2.5rem and at 6rem+.
                  The parent header row grows to 80px when any column falls into this tier
                  (see colouredHeaderHeightPx in BrickWeekSchedule), so `text-sm` reads comfortably. */}
              <span
                aria-hidden
                className={`hidden @[2.5rem]:inline @[6rem]:hidden text-sm leading-none whitespace-nowrap overflow-hidden max-h-full py-1 ${shuttered ? "text-gray-600" : ""}`}
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                {room.name}
              </span>
              {!shuttered && <CircleQuestionMark className="hidden @[6rem]:block w-3 h-3" />}
            </div>
          </div>
        );
      })}
    </>
  );
};

interface AmenityColumnHeadersProps {
  amenities: Amenity[];
}

export const AmenityColumnHeaders = ({ amenities }: AmenityColumnHeadersProps) => {
  return (
    <>
      {amenities.map((amenity) => {
        // Use a different color scheme for amenities (e.g., purple-ish tones)
        const _bg = "bg-gray-500/40";
        // Keep numbers whole so numbered amenities (e.g. car parking spaces 14/15/16) stay distinct
        // in the narrow header instead of all collapsing to the same "CPS1" via plain getInitials.
        const initials = getAmenityColumnLabel(amenity.name);
        // Optional per-location description (e.g. "stored in the hall outside the Amber/Indigo
        // room") so members can tell WHICH bookable amenity they've booked. Set in Practice
        // Settings → amenity → Custom Description; absent for most amenities.
        const description = amenity.description?.trim();
        // Native title carries the description across every responsive tier (incl. the narrow /
        // vertical columns that have no styled tooltip); newline renders as a second line.
        const nativeTitle = description ? `${amenity.name}\n${description}` : amenity.name;
        // Mirrors RoomColumnHeaders' three-tier responsive rule:
        //  - ≥ 6rem:   horizontal initials (with name + description tooltip).
        //  - 2.5–6rem: initials rotated vertical.
        //  - < 2.5rem: coloured bar only.
        return (
          <div
            className={`@container text-center text-sm flex-1 truncate overflow-hidden ${_bg}`}
            key={amenity.id}
            aria-label={`Column showing ${amenity.name}'s schedule.`}
            title={nativeTitle}
          >
            <div className="pt-1 h-full flex justify-center items-center gap-1">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="hidden @[6rem]:inline">{initials}</span>
                  </TooltipTrigger>
                  <TooltipContent align="center" className="max-w-[220px]">
                    <div className="font-semibold">{amenity.name}</div>
                    {description && <div className="mt-0.5 text-xs opacity-80 whitespace-normal">{description}</div>}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Vertical initials — matches the room-column header tier so MC/stool etc. read
                  sideways when columns are in the 2.5–6rem range (header row is 80px then). */}
              <span
                aria-hidden
                className="hidden @[2.5rem]:inline @[6rem]:hidden text-sm leading-none whitespace-nowrap overflow-hidden max-h-full py-1"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                {initials}
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
};
